import { useState } from 'react'
import { api } from '@lib/orpc-client'
import * as Sentry from '@sentry/react'

// The product-category enum the recent-destinations endpoint expects (spec in-scope set).
export type RecentDestinationCategory =
  | 'MOBILE_PREPAID'
  | 'MOBILE_DATA_PACKAGE'
  | 'MOBILE_POSTPAID'
  | 'ELECTRICITY_PREPAID'
  | 'ELECTRICITY_POSTPAID'

export type RecentDestination = {
  customer_no: string
  display_no: string | null
  category: string
  provider: { id: string; name: string; image_url: string | null } | null
  last_used_at: string
}

// Convert a stored MSISDN (62… / +62… / 08…) to the local 0-prefixed form for display.
// Non-phone identifiers (e.g. PLN meter IDs) are returned unchanged.
export const toLocalPhoneNumber = (value: string): string => {
  if (typeof value !== 'string') return ''
  const digits = value.replace(/^\+/, '')
  if (digits.startsWith('62')) return '0' + digits.slice(2)
  return digits
}

// Keep only well-formed rows. Pages map `customer_no` straight into display/format
// helpers, so a malformed or partial payload must never reach them.
const sanitizeDestinations = (raw: unknown): RecentDestination[] =>
  Array.isArray(raw)
    ? raw.filter(
        (d): d is RecentDestination =>
          typeof d?.customer_no === 'string' && d.customer_no.length > 0,
      )
    : []

export const useRecentDestinations = () => {
  const [destinations, setDestinations] = useState<RecentDestination[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchRecentDestinations = async (
    category: RecentDestinationCategory,
    limit = 5,
  ): Promise<RecentDestination[]> => {
    setIsLoading(true)
    try {
      const response = (await api.wallet.ppob.recentDestinations({ category, limit })) as {
        data?: unknown
      }
      const data = sanitizeDestinations(response?.data)
      setDestinations(data)
      setIsLoading(false)
      return data
    } catch (err: any) {
      // Endpoint absent (e.g. FE deployed before BE) or any error: degrade gracefully
      // to no recents rather than crashing the input screen.
      Sentry.captureException(err, {
        tags: { feature: 'ppob', service: 'recentDestinations' },
        extra: { category },
      })
      setDestinations([])
      setIsLoading(false)
      return []
    }
  }

  return { destinations, isLoading, fetchRecentDestinations }
}
